import { useCallback } from 'react';
import type { CreditBlockClient, BureauCredential } from '../../../types';
import { USE_GOBODO_API } from '../../../utils/gobodoUtils';
import { getCustomSuffix } from '../../../config/gobodoConfig';
import { createRecord, updateRecord } from '../../../services/recordStorageService';
import {
  applyCreditBlockRecordTypeFields,
  findCreditBlockRecordTypeTarget,
} from '../../../services/creditBlock/creditBlockCaseGraphRelations';
import {
  buildCreditBlockCaseCreatePayload,
  graphKeyFromCreatedRecord,
} from '../../../services/creditBlock/creditBlockCaseCreate';
import { CREDIT_BLOCK_SYSTEM_CASE_ORIGIN } from '../../../services/creditBlock/caseOriginDisplay';
import { createUnlockRequestRecordsForClient } from '../../../services/queries/creditBlockBureauQueries';
import type { UseMutationResult } from '@tanstack/react-query';
import type { SendEmailInput } from '../../../hooks/useSendEmail';
import type { SendEmailResponse } from '../../../services/emailSendService';
import { BUREAU_LABELS } from '../creditBlockClientConstants';
import type { ToolbarRequestType } from '../creditBlockToolbar/creditBlockToolbarFlowUtils';
import {
  toolbarFlowNoneCreatedMessage,
  toolbarFlowPartialMessage,
  toolbarFlowStartedMessage,
  toolbarFlowSuccessMessage,
} from '../creditBlockToolbar/creditBlockToolbarToasts';

export type { ToolbarRequestType };
export { resolveEndDateFromLength, formatToolbarBureauList, TOOLBAR_FLOW_SHORT_TITLE } from '../creditBlockToolbar/creditBlockToolbarFlowUtils';

type AckEmailMutate = UseMutationResult<SendEmailResponse, Error, SendEmailInput, unknown>['mutate'];

type BureauKey = 'equifax' | 'experian' | 'transunion' | 'chexsystems';

export interface CreditBlockToolbarFlowFormState {
  bureauCredentials: Record<string, BureauCredential>;
}

export interface UseCreditBlockToolbarFlowsParams {
  client: CreditBlockClient;
  formData: CreditBlockToolbarFlowFormState;
  bureauCredentials: Record<string, BureauCredential>;
  caseCreating: boolean;
  setCaseCreating: (v: boolean) => void;
  setToolbarFlowProgress: (
    v: {
      flowTitle: string;
      phase: string;
      bureau?: string;
      step?: number;
      total?: number;
    } | null
  ) => void;
  setActiveToolbarSubmit: (v: ToolbarRequestType | null) => void;
  closeToolbarModal: (requestType: ToolbarRequestType) => void;
  refetchCases: () => Promise<unknown>;
  refetchBureaus: () => Promise<unknown>;
  refetchHistory: () => Promise<unknown>;
  showToast: (message: string, type?: 'success' | 'error') => void;
  sendAckEmailMutate: AckEmailMutate;
}

export function useCreditBlockToolbarFlows({
  client,
  formData,
  bureauCredentials,
  caseCreating,
  setCaseCreating,
  setToolbarFlowProgress,
  setActiveToolbarSubmit,
  closeToolbarModal,
  refetchCases,
  refetchBureaus,
  refetchHistory,
  showToast,
  sendAckEmailMutate,
}: UseCreditBlockToolbarFlowsParams) {
  const refreshToolbarData = useCallback(async () => {
    await Promise.allSettled([refetchCases(), refetchBureaus(), refetchHistory()]);
  }, [refetchCases, refetchBureaus, refetchHistory]);

  const _createCaseAndLinkedRequestsInner = useCallback(
    async (params: {
      requestType: ToolbarRequestType;
      bureaus: BureauKey[];
      source: string;
      startDate?: string;
      endDate?: string;
      endLength?: string;
    }) => {
      const ownerName = '';
      const resolvedOwnerUserId = '';
      const requestTypeLabel: Record<ToolbarRequestType, string> = {
        lock_request: 'Block Bureaus',
        unlock_request: 'Unblock Bureaus',
        permanent_unblock: 'Permanent Unblock',
        extension: 'Unblock Extension',
      };
      const caseIds: string[] = [];
      const failedBureaus: string[] = [];
      const flowTitle = requestTypeLabel[params.requestType];
      const totalBureaus = params.bureaus.length;
      setToolbarFlowProgress({
        flowTitle,
        phase: USE_GOBODO_API ? 'Resolving Credit Block record type…' : 'Preparing requests…',
        total: totalBureaus,
        step: 0,
      });
      const recordTypeTarget = USE_GOBODO_API ? await findCreditBlockRecordTypeTarget().catch(() => null) : null;

      const caseSubTypeByRequestType = (bureauName: string): string => {
        switch (params.requestType) {
          case 'lock_request':
            return `${bureauName} Block`;
          case 'unlock_request':
            return `${bureauName} Unblock`;
          case 'permanent_unblock':
            return `${bureauName} Permanent Unblock`;
          case 'extension':
            return `${bureauName} Unblock`;
          default:
            return `${bureauName} Block`;
        }
      };

      for (let i = 0; i < params.bureaus.length; i++) {
        const bureau = params.bureaus[i];
        const bureauName = BUREAU_LABELS[bureau];
        setToolbarFlowProgress({
          flowTitle,
          phase: `Creating case for ${bureauName}…`,
          bureau: bureauName,
          step: i + 1,
          total: totalBureaus,
        });
        try {
          const subject = `${client.accountName} · ${bureauName} · ${flowTitle}`;
          const description = [
            `Created from Credit Bureaus toolbar (${flowTitle}).`,
            `Owner: ${ownerName || 'Unassigned'}.`,
            `Bureau: ${bureauName}.`,
            `Request source: ${params.source || 'None'}.`,
            params.startDate ? `Start date: ${params.startDate}.` : '',
            params.endDate ? `End date: ${params.endDate}.` : '',
            params.endLength ? `End date length: ${params.endLength}.` : '',
          ]
            .filter(Boolean)
            .join(' ');

          const casePayload = buildCreditBlockCaseCreatePayload({
            clientId: client.id,
            clientName: client.accountName,
            owner: ownerName,
            ownerUserId: resolvedOwnerUserId,
            subject,
            subType: caseSubTypeByRequestType(bureauName),
            priority: 'High',
            origin: CREDIT_BLOCK_SYSTEM_CASE_ORIGIN,
            description,
          });
          if (params.endDate && (params.requestType === 'unlock_request' || params.requestType === 'extension')) {
            casePayload.Reblock_Date__c = params.endDate;
            casePayload.reblockDate = params.endDate;
          }
          if (USE_GOBODO_API) {
            applyCreditBlockRecordTypeFields(casePayload, recordTypeTarget);
          }
          const caseRec = await createRecord('Case__c', casePayload as Record<string, unknown>);
          const caseId = graphKeyFromCreatedRecord(caseRec as { id?: unknown; key?: unknown });

          setToolbarFlowProgress({
            flowTitle,
            phase: `Filing bureau request for ${bureauName}…`,
            bureau: bureauName,
            step: i + 1,
            total: totalBureaus,
          });
          await createUnlockRequestRecordsForClient(client.id, {
            bureau,
            requestType: params.requestType,
            reason: `Source: ${params.source || 'None'}${params.endDate ? ` · End date: ${params.endDate}` : ''}${params.endLength ? ` · End length: ${params.endLength}` : ''}`,
            startDate: params.startDate,
            endDate: params.endDate,
            caseId: caseId || undefined,
          });
          const trimmedCaseId = String(caseId ?? '').trim();
          if (trimmedCaseId) caseIds.push(trimmedCaseId);
        } catch {
          failedBureaus.push(bureauName);
        }
      }

      if (params.startDate || params.endDate || params.requestType === 'permanent_unblock') {
        const skipStartDate = params.requestType === 'extension';
        const dateUpdates = params.bureaus
          .map((b) => ({
            creditBureauId:
              formData.bureauCredentials[b]?.creditBureauId ||
              bureauCredentials[b]?.creditBureauId ||
              '',
            startDate: skipStartDate ? undefined : params.startDate,
            endDate: params.endDate,
            ...(params.requestType === 'permanent_unblock'
              ? { permUnblockDate: new Date().toISOString().slice(0, 10) }
              : {}),
          }))
          .filter((u) => u.creditBureauId);
        if (dateUpdates.length > 0) {
          setToolbarFlowProgress({
            flowTitle,
            phase: 'Updating bureau dates on file…',
            total: totalBureaus,
            step: totalBureaus,
          });
          await Promise.allSettled(
            dateUpdates.map(({ creditBureauId, startDate, endDate, permUnblockDate }) => {
              const payload: Record<string, unknown> = {};
              if (startDate) {
                payload.StartDate__c = startDate;
                payload.Start_Date__c = startDate;
                payload.EnrollmentDate__c = startDate;
              }
              if (endDate) {
                payload.EndDate__c = endDate;
                payload.End_Date__c = endDate;
                payload.UnblockDate__c = endDate;
              }
              if (permUnblockDate) {
                payload.PermUnblockDate__c = permUnblockDate;
                payload.Perm_Unblock_Date__c = permUnblockDate;
                const suffix = USE_GOBODO_API ? getCustomSuffix() || '__c' : '__c';
                if (suffix !== '__c') {
                  payload[`PermUnblockDate${suffix}`] = permUnblockDate;
                  payload[`Perm_Unblock_Date${suffix}`] = permUnblockDate;
                }
              }
              return updateRecord(`Credit_Bureaus${USE_GOBODO_API ? getCustomSuffix() || '__c' : '__c'}`, creditBureauId, payload);
            })
          );
        }
      }

      const bureauLabel = params.bureaus.map((b) => BUREAU_LABELS[b]).join(', ');
      const clientEmail = client.contact?.email?.trim();
      if (clientEmail) {
        setToolbarFlowProgress({
          flowTitle,
          phase: 'Queueing confirmation email…',
          total: totalBureaus,
          step: totalBureaus,
        });
        sendAckEmailMutate({
          to: [clientEmail],
          subject: `Request Received — ${client.accountName} · ${requestTypeLabel[params.requestType]}`,
          body: [
            `Dear ${client.accountName || 'Client'},`,
            '',
            `Your ${requestTypeLabel[params.requestType].toLowerCase()} request has been received and ${caseIds.length} case${caseIds.length !== 1 ? 's have' : ' has'} been created.`,
            '',
            `Case IDs: ${caseIds.join(', ') || 'Pending'}`,
            `Request Type: ${requestTypeLabel[params.requestType]}`,
            `Bureaus: ${bureauLabel}`,
            '',
            'Our team will process your request and you will receive a confirmation once completed.',
            '',
            'Thank you,',
            'Regal Credit Block Operations',
          ].join('\n'),
        });
      }

      return {
        caseId: caseIds[caseIds.length - 1] ?? '',
        label: requestTypeLabel[params.requestType],
        createdCount: caseIds.length,
        requestedCount: params.bureaus.length,
        failedBureaus,
        failedCount: failedBureaus.length,
      };
    },
    [
      client,
      formData.bureauCredentials,
      bureauCredentials,
      setToolbarFlowProgress,
      sendAckEmailMutate,
    ]
  );

  const createCaseAndLinkedRequests = useCallback(
    async (params: {
      requestType: ToolbarRequestType;
      bureaus: BureauKey[];
      source: string;
      startDate?: string;
      endDate?: string;
      endLength?: string;
    }) => {
      if (caseCreating) {
        return {
          caseId: '',
          label: '',
          createdCount: 0,
          requestedCount: 0,
          failedBureaus: [] as string[],
          failedCount: 0,
        };
      }
      setCaseCreating(true);
      try {
        return await _createCaseAndLinkedRequestsInner(params);
      } finally {
        setToolbarFlowProgress(null);
        setCaseCreating(false);
      }
    },
    [caseCreating, _createCaseAndLinkedRequestsInner, setCaseCreating, setToolbarFlowProgress]
  );

  const runToolbarRequestFlow = useCallback(
    async (params: {
      requestType: ToolbarRequestType;
      bureaus: BureauKey[];
      source: string;
      startDate?: string;
      endDate?: string;
      endLength?: string;
      failureMessage: string;
    }) => {
      if (caseCreating) return;
      setActiveToolbarSubmit(params.requestType);
      showToast(toolbarFlowStartedMessage(params.requestType, params.bureaus));
      try {
        const { createdCount, requestedCount, failedBureaus, failedCount } = await createCaseAndLinkedRequests({
          requestType: params.requestType,
          bureaus: params.bureaus,
          source: params.source,
          startDate: params.startDate,
          endDate: params.endDate,
          endLength: params.endLength,
        });
        if (createdCount > 0 && failedCount === 0) {
          showToast(toolbarFlowSuccessMessage(params.requestType, createdCount, params.bureaus));
          closeToolbarModal(params.requestType);
        } else if (createdCount > 0 && failedCount > 0) {
          showToast(
            toolbarFlowPartialMessage(params.requestType, createdCount, requestedCount, params.bureaus, failedBureaus),
            'error'
          );
          closeToolbarModal(params.requestType);
        } else {
          showToast(toolbarFlowNoneCreatedMessage(params.requestType, params.bureaus), 'error');
        }
        if (createdCount > 0) {
          void refreshToolbarData();
        }
      } catch {
        showToast(params.failureMessage, 'error');
      } finally {
        setActiveToolbarSubmit(null);
      }
    },
    [
      caseCreating,
      setActiveToolbarSubmit,
      showToast,
      createCaseAndLinkedRequests,
      closeToolbarModal,
      refreshToolbarData,
    ]
  );

  return {
    refreshToolbarData,
    runToolbarRequestFlow,
    createCaseAndLinkedRequests,
  };
}
